/**
 * ════════════════════════════════════════════════════════════════════════════════
 * Budget CRUD Operations
 * Extracted from budgetService.js for better modularity
 * Basic Create, Read, Update, Delete operations for budgets
 * ════════════════════════════════════════════════════════════════════════════════
 */

import { db, auth } from '@/config/firebase';
import { 
  collection, 
  doc, 
  getDocs, 
  getDoc, 
  addDoc, 
  updateDoc, 
  deleteDoc,
  setDoc,
  query,
  where,
  orderBy,
  Timestamp,
  writeBatch
} from 'firebase/firestore';
import { 
  BUDGET_CADENCE, 
  BUDGET_TEMPLATES, 
  BUDGET_STATUS, 
  ROLLOVER_RULES 
} from './budgetConstants';

// Debug logging - only in dev with VITE_DEBUG_BUDGET=true
const DEBUG_BUDGET = import.meta.env.DEV && import.meta.env.VITE_DEBUG_BUDGET === 'true';

// ============================================================================
// INTERNAL REFERENCE HELPERS
// ============================================================================

/**
 * Get user's budgets collection reference
 * @param {string} userId 
 * @returns {CollectionReference}
 */
export function getUserBudgetsRef(userId) {
  return collection(db, 'users', userId, 'budgets');
}

/**
 * Get budget lines subcollection reference
 * @param {string} userId 
 * @param {string} budgetId 
 * @returns {CollectionReference}
 */
export function getBudgetLinesRef(userId, budgetId) {
  return collection(db, 'users', userId, 'budgets', budgetId, 'lines');
}

/**
 * Get budget summary document reference
 * @param {string} userId 
 * @param {string} budgetId 
 * @returns {DocumentReference}
 */
export function getBudgetSummaryRef(userId, budgetId) {
  return doc(db, 'users', userId, 'budgets', budgetId, 'meta', 'summary');
}

// ============================================================================
// BUDGET CRUD OPERATIONS
// ============================================================================

/**
 * Create a new budget
 * @param {object} budgetData 
 * @returns {Promise<object>}
 */
export async function createBudget(budgetData) {
  try {
    const userId = auth.currentUser?.uid;
    if (!userId) throw new Error('User not authenticated');

    const budgetsRef = getUserBudgetsRef(userId);
    const timestamp = Timestamp.now();

    const budget = {
      name: budgetData.name || 'My Budget',
      mode: budgetData.mode || 'INDIVIDUAL',
      cadence: budgetData.cadence || BUDGET_CADENCE.MONTHLY,
      startDate: budgetData.startDate || timestamp,
      endDate: budgetData.endDate || timestamp,
      template: budgetData.template || BUDGET_TEMPLATES.CUSTOM,
      rolloverRule: budgetData.rolloverRule || {
        surplus: ROLLOVER_RULES.CARRY,
        deficit: ROLLOVER_RULES.CARRY
      },
      status: BUDGET_STATUS.OPEN,
      members: budgetData.members || null,
      // FINLIST FIX: Store monthly income and custom budget amount for Custom budgets
      monthlyIncome: budgetData.monthlyIncome || 0,
      customBudgetAmount: budgetData.customBudgetAmount || 0,
      createdAt: timestamp,
      updatedAt: timestamp,
      // Legacy compatibility fields
      month: budgetData.month || null,
      periodType: budgetData.cadence?.toLowerCase() || 'monthly'
    };

    const docRef = await addDoc(budgetsRef, budget);

    // Initialize summary with user-provided values for Custom budgets
    const isCustomBudget = budgetData.template === BUDGET_TEMPLATES.CUSTOM;
    
    // FINLIST FIX: Calculate auto-savings for Custom budgets
    // Auto-savings = Income - Budget Amount (what's left after budget allocation)
    const autoSavingsPlanned = isCustomBudget 
      ? Math.max(0, (budgetData.monthlyIncome || 0) - (budgetData.customBudgetAmount || 0))
      : 0;
    
    // FINLIST FIX: For Custom budgets, Safe to Spend = Budget Amount (your spending limit)
    const initialSafeToSpend = isCustomBudget ? (budgetData.customBudgetAmount || 0) : 0;
    
    const summaryRef = getBudgetSummaryRef(userId, docRef.id);
    await setDoc(summaryRef, {
      // FINLIST FIX: Use user's monthly income as planned income for Custom budgets
      incomePlanned: isCustomBudget ? (budgetData.monthlyIncome || 0) : 0,
      incomeActual: 0,
      // FINLIST FIX: Use user's custom budget amount as planned expenses for Custom budgets
      expensePlanned: isCustomBudget ? (budgetData.customBudgetAmount || 0) : 0,
      expenseActual: 0,
      // FINLIST FIX: Auto-calculate savings for Custom budgets (Income - Budget)
      savingsPlanned: autoSavingsPlanned,
      // FINLIST FIX: Safe to Spend = Budget Amount for Custom budgets
      safeToSpend: initialSafeToSpend,
      burnRatePct: 0,
      projectedEOM: 0,
      alerts: [],
      lastCalculated: timestamp
    });

    console.log('[BUDGET CRUD] Budget created:', docRef.id);
    return { id: docRef.id, ...budget };
  } catch (error) {
    console.error('[BUDGET CRUD] Error creating budget:', error);
    throw error;
  }
}

/**
 * Get a budget by ID
 * @param {string} userId 
 * @param {string} budgetId 
 * @returns {Promise<object|null>}
 */
export async function getBudget(userId, budgetId) {
  try {
    if (!userId) throw new Error('User ID required');
    if (!budgetId) throw new Error('Budget ID required');

    const budgetRef = doc(db, 'users', userId, 'budgets', budgetId);
    const budgetDoc = await getDoc(budgetRef);

    if (!budgetDoc.exists()) {
      return null;
    }

    const budgetData = { id: budgetDoc.id, ...budgetDoc.data() };

    // Get lines
    const linesRef = getBudgetLinesRef(userId, budgetId);
    const linesSnapshot = await getDocs(linesRef);
    budgetData.lines = linesSnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));

    // Get summary
    const summaryRef = getBudgetSummaryRef(userId, budgetId);
    const summaryDoc = await getDoc(summaryRef);
    budgetData.summary = summaryDoc.exists() ? summaryDoc.data() : null;

    return budgetData;
  } catch (error) {
    console.error('[BUDGET CRUD] Error fetching budget:', error);
    throw error;
  }
}

/**
 * Apply a rebalance plan to budget lines (planned amounts only) atomically
 * @param {string} userId
 * @param {string} budgetId
 * @param {object} plan - output of generateRebalancePlan
 */
export async function applyRebalancePlan(userId, budgetId, plan) {
  if (!userId || !budgetId) throw new Error('userId and budgetId are required');
  if (!plan) throw new Error('plan is required');

  const batch = writeBatch(db);
  const linesRef = getBudgetLinesRef(userId, budgetId);

  // Aggregate changes per envelope
  const deltas = {};
  const addDelta = (id, amount) => {
    if (!deltas[id]) deltas[id] = 0;
    deltas[id] += amount;
  };

  (plan.transfers || []).forEach((t) => {
    addDelta(t.fromEnvelopeId, -Math.abs(t.amount));
    addDelta(t.toEnvelopeId, Math.abs(t.amount));
  });

  (plan.topUps || []).forEach((t) => {
    addDelta(t.toEnvelopeId, Math.abs(t.amount));
  });

  const now = Timestamp.now();

  Object.entries(deltas).forEach(([lineId, delta]) => {
    const lineDoc = doc(linesRef, lineId);
    batch.update(lineDoc, {
      planned: (plan.beforeAfter?.[lineId]?.budgetedAfter ?? null) || null,
      updatedAt: now,
    });
  });

  // Rebalance event log
  const eventsRef = collection(db, 'users', userId, 'budgets', budgetId, 'rebalanceEvents');
  const eventDoc = doc(eventsRef);
  batch.set(eventDoc, {
    id: eventDoc.id,
    createdAt: now,
    mode: plan.mode,
    totals: plan.totals,
    transfers: plan.transfers,
    topUps: plan.topUps,
    beforeAfter: plan.beforeAfter,
  });

  await batch.commit();
  return { eventId: eventDoc.id };
}

/**
 * Get all budgets for a user
 * @param {string} userId 
 * @param {object} filters - Optional filters (status, cadence, etc.)
 * @returns {Promise<array>}
 */
export async function getUserBudgets(userId, filters = {}) {
  try {
    if (!userId) throw new Error('User ID required');

    const buildQuery = (withOrderBy = true) => {
      let q = getUserBudgetsRef(userId);

      if (filters.status) {
        q = query(q, where('status', '==', filters.status));
      }

      if (filters.cadence) {
        q = query(q, where('cadence', '==', filters.cadence));
      }

      if (withOrderBy) {
        // BUDGET-CENTRIC NAV: Order by startDate DESC so newest budget periods appear first
        // This allows chronological navigation between actual budget documents
        q = query(q, orderBy('startDate', 'desc'));
      }

      return q;
    };

    // GUARDRAIL: Budget queries must ALWAYS be indexed.
    // Do NOT reintroduce client-side sorting or fallback logic.
    try {
      const snapshot = await getDocs(buildQuery(true));
      const budgets = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      console.log('[BUDGET CRUD] Budgets loaded (ordered by startDate desc):', budgets.length);
      return budgets;
    } catch (error) {
      if (error.code === 'failed-precondition') {
        // Index missing for startDate - fallback to createdAt and sort client-side
        console.warn('[BUDGET CRUD] startDate index missing, falling back to createdAt ordering');
        try {
          const fallbackQuery = query(
            getUserBudgetsRef(userId),
            ...(filters.status ? [where('status', '==', filters.status)] : []),
            orderBy('createdAt', 'desc')
          );
          const fallbackSnapshot = await getDocs(fallbackQuery);
          const budgets = fallbackSnapshot.docs.map(doc => ({
            id: doc.id,
            ...doc.data()
          }));
          // Sort client-side by startDate
          budgets.sort((a, b) => {
            const dateA = a.startDate?.toDate?.() || new Date(a.startDate) || new Date(0);
            const dateB = b.startDate?.toDate?.() || new Date(b.startDate) || new Date(0);
            return dateB - dateA; // DESC order
          });
          console.log('[BUDGET CRUD] Budgets loaded (client-sorted by startDate):', budgets.length);
          return budgets;
        } catch (fallbackError) {
          console.error('[BUDGET CRUD] Fallback query also failed:', fallbackError);
          throw fallbackError;
        }
      } else {
        throw error;
      }
    }

  } catch (error) {
    console.error('[BUDGET CRUD] Error fetching budgets:', error);
    throw error;
  }
}

/**
 * Update a budget
 * @param {string} userId 
 * @param {string} budgetId 
 * @param {object} updates 
 * @returns {Promise<object>}
 */
export async function updateBudget(userId, budgetId, updates) {
  try {
    if (!userId) throw new Error('User ID required');
    if (!budgetId) throw new Error('Budget ID required');

    const budgetRef = doc(db, 'users', userId, 'budgets', budgetId);
    const updateData = {
      ...updates,
      updatedAt: Timestamp.now()
    };

    await updateDoc(budgetRef, updateData);

    return { id: budgetId, ...updateData };
  } catch (error) {
    console.error('[BUDGET CRUD] Error updating budget:', error);
    throw error;
  }
}

/**
 * Delete a budget
 * @param {string} userId 
 * @param {string} budgetId 
 * @returns {Promise<void>}
 */
export async function deleteBudget(userId, budgetId) {
  try {
    if (!userId) throw new Error('User ID required');
    if (!budgetId) throw new Error('Budget ID required');

    // Delete lines
    const linesRef = getBudgetLinesRef(userId, budgetId);
    const linesSnapshot = await getDocs(linesRef);
    const batch = writeBatch(db);

    linesSnapshot.docs.forEach(doc => {
      batch.delete(doc.ref);
    });

    // Delete summary
    const summaryRef = getBudgetSummaryRef(userId, budgetId);
    batch.delete(summaryRef);

    // Delete budget
    const budgetRef = doc(db, 'users', userId, 'budgets', budgetId);
    batch.delete(budgetRef);

    await batch.commit();
    console.log('[BUDGET CRUD] Budget deleted:', budgetId);
  } catch (error) {
    console.error('[BUDGET CRUD] Error deleting budget:', error);
    throw error;
  }
}

/**
 * Get the active/current budget for a user
 * FINLIST SSOT: Returns the most recent OPEN budget for the current period
 * 
 * @param {string} userId - User ID
 * @returns {Promise<object|null>} Active budget or null
 */
export async function getActiveBudget(userId) {
  // FINLIST SSOT: unified category-envelope atomic wiring (global-scale perf target)
  try {
    if (!userId) throw new Error('User ID required');

    if (DEBUG_BUDGET) console.log('[BUDGET CRUD SSOT] Getting active budget for user:', userId);

    // Get all OPEN budgets, ordered by startDate (most recent first)
    const budgetsRef = getUserBudgetsRef(userId);
    
    // Try with startDate ordering first (budget-centric navigation)
    try {
      const q = query(
        budgetsRef, 
        where('status', '==', BUDGET_STATUS.OPEN),
        orderBy('startDate', 'desc')
      );
      
      const snapshot = await getDocs(q);
      
      if (!snapshot.empty) {
        const budgetDoc = snapshot.docs[0];
        const budget = {
          id: budgetDoc.id,
          ...budgetDoc.data()
        };
        
        if (DEBUG_BUDGET) console.log('[BUDGET CRUD SSOT] Active budget found (startDate order):', budget.id, budget.name);
        return budget;
      }
    } catch (indexError) {
      console.warn('[BUDGET CRUD SSOT] startDate index not available, trying createdAt...');
    }
    
    // Fallback: try with createdAt ordering
    try {
      const q = query(
        budgetsRef, 
        where('status', '==', BUDGET_STATUS.OPEN),
        orderBy('createdAt', 'desc')
      );
      
      const snapshot = await getDocs(q);
      
      if (!snapshot.empty) {
        const budgetDoc = snapshot.docs[0];
        const budget = {
          id: budgetDoc.id,
          ...budgetDoc.data()
        };
        
        if (DEBUG_BUDGET) console.log('[BUDGET CRUD SSOT] Active budget found (createdAt order):', budget.id, budget.name);
        return budget;
      }
    } catch (indexError) {
      console.warn('[BUDGET CRUD SSOT] createdAt index not available, trying no ordering...');
    }
    
    // Last resort: get all budgets and filter/sort client-side
    const allSnapshot = await getDocs(budgetsRef);
    const openBudgets = allSnapshot.docs
      .map(doc => ({ id: doc.id, ...doc.data() }))
      .filter(b => b.status === BUDGET_STATUS.OPEN)
      .sort((a, b) => {
        const aDate = a.startDate?.toDate?.()?.getTime() || a.createdAt?.toDate?.()?.getTime() || 0;
        const bDate = b.startDate?.toDate?.()?.getTime() || b.createdAt?.toDate?.()?.getTime() || 0;
        return bDate - aDate;
      });
    
    if (openBudgets.length > 0) {
      if (DEBUG_BUDGET) console.log('[BUDGET CRUD SSOT] Active budget found (client-side sort):', openBudgets[0].id, openBudgets[0].name);
      return openBudgets[0];
    }
    
    if (DEBUG_BUDGET) console.log('[BUDGET CRUD SSOT] No active (OPEN) budget found for user');
    return null;
  } catch (error) {
    console.error('[BUDGET CRUD SSOT] Error getting active budget:', error);
    return null;
  }
}
